import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Col, Container, Image, OverlayTrigger, Row, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faCertificate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useParams } from 'react-router-dom';

import ActionDropDown from 'components/shared/ActionDropDown';
import { anchovy, backgroundGrey4, contentVerifiedBlue, iconGrey } from 'lib/css/colors';
import CloneVendorProductModal from 'components/global_products/detail_view/CloneVendorProductModal';
import { CONTENT_VERIFICATION_STATUSES } from 'lib/enums/ContentVerificationStatus';
import EditableFieldV2 from 'components/shared/EditableFieldV2';
import FlagComponent from 'components/global_products/detail_view/FlagComponent';
import {
  getURL,
  GLOBAL_PRODUCT,
  GLOBAL_PRODUCT_RESET_CACHE,
  GLOBAL_PRODUCT_VENDOR_MARK_AS_DEMO,
} from 'lib/networking/endpoints';
import { GLOBAL_PRODUCT_TYPE, ProductType } from 'lib/enums/ProductType';
import GlobalProductAssetView from 'components/global_products/detail_view/assets_view/GlobalProductAssetView';
import GlobalProductsChangelogView from 'components/global_products/detail_view/GlobalProductsChangelogView';
import { GlobalProductsDetailContext } from 'components/global_products/detail_view/GlobalProductsDetailContainer';
import GlobalProductsRelationshipsView from 'components/global_products/detail_view/GlobalProductsRelationshipsView';
import GlobalProductUpdateModal from 'components/global_products/detail_view/GlobalProductUpdateModal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import NationalSkuBadge from 'components/global_products/detail_view/NationalSkuBadge';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { PRODUCT_DETAIL_VIEW_TABS } from 'lib/constants';
import ProductsAttributesView from 'components/global_products/detail_view/attributes_view/ProductsAttributesView';
import ProductScoreCard from 'components/global_products/ProductScoreCard';
import InfluenceCampaignStatus from 'components/upb_viewer/InfluenceCampaignStatus';
import RejectedGtinsSection from 'components/global_products/detail_view/RejectedGtinsSection';
import UniproUpbToggle from 'components/upb_viewer/UniproUpbToggle';
import UPBProductDetailsPage from 'components/upb_viewer/UPBProductDetailsPage';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import usePut from 'lib/hooks/usePut';
import useQueryString from 'lib/hooks/useQueryString';

// Only falls back to the Global Product's own source when the Global Product
// option itself is selected -- a specific manufacturer/distributor product
// with no source of its own must show nothing, not the parent's source under
// its own "Original Data Source" label.
export function resolveDisplayedDataSource(selectedProductOption, selectedProduct, globalProduct) {
  const isGlobalOptionSelected = selectedProductOption?.productType === GLOBAL_PRODUCT_TYPE;
  return isGlobalOptionSelected
    ? selectedProduct?.data_source_origin || globalProduct?.data_source_origin
    : selectedProduct?.data_source_origin;
}

function GlobalProductsDetailView() {
  const { globalProductId, productID, productType } = useParams();
  const tab = useQueryString().get('tab');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(tab || PRODUCT_DETAIL_VIEW_TABS.PDP_PREVIEW);
  const [searchInput, setSearchInput] = useState('');
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const preservedSelection = useRef({ productType: null, productId: null });
  const [showGlobalProductUpdateModal, setShowGlobalProductUpdateModal] = useState(false);
  const [globalProductIdToUpdate, setGlobalProductIdToUpdate] = useState(null);
  const [flagReloadKey, setFlagReloadKey] = useState(0);

  const {
    setOtherImages,
    thumbnailImage,
    setThumbnailImage,
    setLoading,
    cndProductScore,
    p0Score,
  } = useContext(GlobalProductsDetailContext);
  const selectedVendorProductId =
    selectedProductOption?.productType === ProductType.DISTRIBUTOR
      ? selectedProductOption?.productId
      : null;
  const selectedManufacturerProductId =
    selectedProductOption?.productType === ProductType.MANUFACTURER
      ? selectedProductOption?.productId
      : null;

  const isADemoProduct =
    selectedProductOption?.productType === ProductType.DISTRIBUTOR
      ? selectedProductOption?.product.is_demo_product
      : null;

  const {
    data: { data: globalProduct } = {},
    loading,
    error,
    refetch,
  } = useGet(getURL(GLOBAL_PRODUCT, { productId: globalProductId }), {}, !globalProductId);

  const { postData: markAsDemoProduct, loading: markingAsDemoProduct } = usePost(
    GLOBAL_PRODUCT_VENDOR_MARK_AS_DEMO,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: isADemoProduct
          ? 'Product marked as not a demo product successfully'
          : 'Product marked as a demo product successfully',
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          `An error occurred while marking the product as a demo: ` +
          ` ${error?.response?.data?.message}`,
      }),
  );

  const { putData: updateGlobalProduct } = usePut(
    getURL(GLOBAL_PRODUCT, { productId: globalProductId }),
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Successfully updated',
        title: 'Success',
      });
      refetch();
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        title: 'Error',
        showCancelButton: true,
        confirmButtonText: 'Proceed And Merge',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.isConfirmed) {
          // access the payload and get the gtin
          const requestData = error?.response?.config?.data;
          const parsedData =
            typeof requestData === 'string' ? JSON.parse(requestData) : requestData;
          const gtin = parsedData?.gtin;
          if (gtin) {
            updateGlobalProduct({ gtin, merge_if_existing: true });
          }
        }
      }),
  );

  const { postData: resetCache, loading: resttingCache } = usePost(
    GLOBAL_PRODUCT_RESET_CACHE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Product cache reset successfully',
      }).then(() => {
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          `An error occurred while resetting the product cache: ` +
          ` ${error?.response?.data?.message}`,
      }),
  );

  const selectedProductOptions = useMemo(() => {
    if (!globalProduct) {
      return [];
    }
    const options = [
      {
        label: 'Global Product',
        value: `global-${globalProductId}`,
        productId: globalProduct.id,
        productType: GLOBAL_PRODUCT_TYPE,
        product: globalProduct,
      },
    ];
    globalProduct?.global_product_manufacturers?.forEach(product =>
      options.push({
        label: `${product.name} (${product.global_manufacturer.name} \
          - ${product.manufacturer_sku})`,
        value: `manufacturer-${product.id}`,
        productId: product.id,
        productType: ProductType.MANUFACTURER,
        product,
      }),
    );
    globalProduct?.global_product_vendors?.forEach(product =>
      options.push({
        label: `${product.name} (${product.global_vendor.name} - ${product.vendor_sku})`,
        value: `vendor-${product.id}`,
        productId: product.id,
        productType: ProductType.DISTRIBUTOR,
        product,
      }),
    );
    return options;
  }, [globalProduct, globalProductId]);

  // Preserve the selection details across refetches
  useEffect(() => {
    if (selectedProductOption) {
      preservedSelection.current = {
        productType: selectedProductOption.productType,
        productId: selectedProductOption.productId,
      };
    }
  }, [selectedProductOption]);

  // Set the product selection using fresh data
  useEffect(() => {
    if (!globalProduct) return;

    // Priority 1: If we have preserved selection, reconstruct it from fresh data
    if (preservedSelection.current.productType && preservedSelection.current.productId) {
      const updatedOption = selectedProductOptions.find(
        option =>
          option.productType === preservedSelection.current.productType &&
          option.productId === preservedSelection.current.productId,
      );
      if (updatedOption) {
        setSelectedProductOption(updatedOption);
        return;
      }
    }

    // Priority 2: If we have URL params and no preserved selection
    if (productID && productType) {
      const matchedProd = selectedProductOptions.find(
        option => option?.productType === productType && option.productId.toString() === productID,
      );
      if (matchedProd) {
        setSelectedProductOption(matchedProd);
        return;
      }
    }

    // Priority 3: Default to global product
    setSelectedProductOption({
      label: 'Global Product',
      value: `global-${globalProduct.id}`,
      productId: globalProduct.id,
      productType: GLOBAL_PRODUCT_TYPE,
      product: globalProduct,
    });
  }, [globalProduct, productID, productType, selectedProductOptions]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !globalProduct) {
    return null;
  }

  const selectedProduct = selectedProductOption?.product;
  const displayedDataSource = resolveDisplayedDataSource(
    selectedProductOption,
    selectedProduct,
    globalProduct,
  );

  return (
    <Container fluid className={classNames(css(styles.container), 'py-3')}>
      <Row>
        <Col sm={3} className={css(styles.leftCol)}>
          <Image
            src={thumbnailImage?.url ?? placeholderProductImage}
            fluid
            className="mx-auto d-block w-100"
            alt="Product"
          />
          <div className="d-flex">
            <h5 className="font-weight-bold py-3">{selectedProduct?.name}</h5>
            <div className="mt-3 ml-2">
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    {selectedProduct?.content_verification_status ===
                    CONTENT_VERIFICATION_STATUSES.VERIFIED
                      ? 'Content verified'
                      : 'Not content verified'}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon
                  icon={faCertificate}
                  color={
                    selectedProduct?.content_verification_status ===
                    CONTENT_VERIFICATION_STATUSES.VERIFIED
                      ? contentVerifiedBlue
                      : iconGrey
                  }
                  style={{ fontSize: '24px' }}
                />
              </OverlayTrigger>
            </div>
          </div>
          <Row className="mt-3">
            <Col className={css(styles.attributeName)}>Product</Col>
          </Row>
          <Row className={css(styles.productSelect)}>
            <Col>
              <Select
                className="w-100"
                value={selectedProductOptions.filter(
                  option => option.value === selectedProductOption?.value,
                )}
                options={selectedProductOptions}
                onChange={option => {
                  setOtherImages([]);
                  setThumbnailImage({});
                  setLoading(true);
                  setSelectedProductOption(option);
                }}
              />
            </Col>
          </Row>
          <div>
            <ProductScoreCard
              score={cndProductScore}
              p0_score={p0Score}
              product={selectedProduct}
              productType={selectedProductOption?.productType}
            />
          </div>
          <div>
            <p className={css(styles.attributeName)}>PCID</p>
            <p className={css(styles.attributeValue)}>{globalProduct?.pcid || '-'}</p>
          </div>
          {selectedProduct?.global_manufacturer ? (
            <div>
              <p className={css(styles.attributeName)}>Manufacturer SKU</p>
              <p className={css(styles.attributeValue)}>{selectedProduct?.manufacturer_sku}</p>
            </div>
          ) : (
            <div>
              <p className={css(styles.attributeName)}>Vendor SKU</p>
              <p className={css(styles.attributeValue)}>
                {selectedProduct?.vendor_sku}
                <NationalSkuBadge
                  vendorProductId={selectedProduct?.id}
                  globalVendor={selectedProduct?.global_vendor}
                />
              </p>
            </div>
          )}
          <div>
            <p className={css(styles.attributeName)}>GTIN</p>
            <EditableFieldV2
              value={globalProduct.gtin || '-'}
              setValue={e => updateGlobalProduct({ gtin: e })}
              editButtonSide="right"
            />
          </div>
          <div className="mt-3 mb-3">
            <FlagComponent
              product={selectedProduct}
              productType={selectedProductOption?.productType}
              onFlagUpdate={() => setFlagReloadKey(key => key + 1)}
            />
          </div>
          <div>
            <p className={css(styles.attributeName)}>GTIN ITEM</p>
            <p className={css(styles.attributeValue)}>{globalProduct.gtin_item || '-'}</p>
          </div>
          <div>
            <p className={css(styles.attributeName)}>UPC</p>
            <p className={css(styles.attributeValue)}>{globalProduct.upc || '-'}</p>
          </div>
          <div>
            <p className={css(styles.attributeName)}>Cut and Dry ID</p>
            <p className={css(styles.attributeValue)}>{selectedProduct?.cnd_id || '-'}</p>
          </div>
          <div>
            <p className={css(styles.attributeName)}>Global Product ID</p>
            <EditableFieldV2
              value={globalProductIdToUpdate || globalProduct.id}
              setValue={e => {
                setGlobalProductIdToUpdate(e);
                setShowGlobalProductUpdateModal(true);
              }}
              editButtonSide="right"
            />
            <p className={css(styles.attributeValue)}>{globalProduct.id}</p>
          </div>
          <div>
            <p className={css(styles.attributeName)}>Created Date</p>
            <p className={css(styles.attributeValue)}>{selectedProduct?.date_created}</p>
          </div>
          <div>
            <p className={css(styles.attributeName)}>Last Modified Date</p>
            <p className={css(styles.attributeValue)}>{selectedProduct?.date_modified}</p>
          </div>
          {displayedDataSource && (
            <div>
              <p className={css(styles.attributeName)}>Original Data Source</p>
              <p className={css(styles.attributeValue)}>{displayedDataSource.name}</p>
            </div>
          )}
          {selectedProduct?.categorized_by && (
            <div>
              <p className={css(styles.attributeName)}>Categorized By</p>
              <p className={css(styles.attributeValue)}>{selectedProduct?.categorized_by?.name}</p>
            </div>
          )}
          {globalProduct?.l4_category ? (
            <div>
              <div>
                <p className={css(styles.attributeName)}>L1 Category</p>
                <p className={css(styles.attributeValue)}>
                  {globalProduct.l4_category.l3_category.l2_category.l1_category.category_name}
                </p>
              </div>
              <div>
                <p className={css(styles.attributeName)}>L2 Category</p>
                <p className={css(styles.attributeValue)}>
                  {globalProduct.l4_category.l3_category.l2_category.category_name}
                </p>
              </div>
              <div>
                <p className={css(styles.attributeName)}>L3 Category</p>
                <p className={css(styles.attributeValue)}>
                  {globalProduct.l4_category.l3_category.category_name}
                </p>
              </div>
              <div>
                <p className={css(styles.attributeName)}>L4 Category</p>
                <p className={css(styles.attributeValue)}>
                  {globalProduct.l4_category.category_name}
                </p>
              </div>
            </div>
          ) : null}
          <div className="mt-3">
            <RejectedGtinsSection selectedProductOption={selectedProductOption} refetch={refetch} />
          </div>
        </Col>
        <Col sm={9}>
          <div className="d-flex justify-content-end align-items-center">
            {selectedManufacturerProductId && (
              <div className="mr-3">
                <InfluenceCampaignStatus productId={selectedManufacturerProductId} />
              </div>
            )}
            {selectedManufacturerProductId && (
              <div className="mr-3">
                <UniproUpbToggle productId={selectedManufacturerProductId} />
              </div>
            )}
            <ActionDropDown
              className="ml-2"
              submenuItems={[
                {
                  title: 'Reset cache of the selected product',
                  action: () =>
                    resetCache({
                      vendor_product_id: selectedVendorProductId,
                      manufacturer_product_id: selectedManufacturerProductId,
                    }),
                  disabled: resttingCache,
                },
                {
                  title: 'Reset cache of all linked products in global product',
                  action: () => resetCache({ global_product_id: globalProductId }),
                  disabled: resttingCache,
                },
                {
                  title: isADemoProduct ? 'Unmark as a demo product' : 'Mark as a demo product',
                  action: () =>
                    markAsDemoProduct({
                      vendor_product_id: selectedVendorProductId,
                      demo_status: !isADemoProduct,
                    }),
                  disabled: markingAsDemoProduct || !selectedVendorProductId,
                },
                {
                  title: 'Clone this product',
                  action: () => setShowCloneModal(true),
                  disabled: !selectedVendorProductId,
                },
              ]}
              label={'Product Actions'}
            />
          </div>
          <Tabs
            className="border-top-0"
            defaultActiveKey={activeTabKey}
            onSelect={selectedKey => setActiveTabKey(selectedKey)}
            transition={false}
          >
            <Tab
              eventKey={PRODUCT_DETAIL_VIEW_TABS.PDP_PREVIEW}
              title={PRODUCT_DETAIL_VIEW_TABS.PDP_PREVIEW}
            >
              <UPBProductDetailsPage
                key={`pdp-${flagReloadKey}`}
                propProductId={selectedProductOption?.productId}
                productType={selectedProductOption?.productType}
                hideHeader
              />
            </Tab>
            <Tab
              eventKey={PRODUCT_DETAIL_VIEW_TABS.ATTRIBUTES_V1}
              title={PRODUCT_DETAIL_VIEW_TABS.ATTRIBUTES_V1}
              tabClassName={
                activeTabKey === PRODUCT_DETAIL_VIEW_TABS.ATTRIBUTES_V1
                  ? css(styles.activeTab)
                  : css(styles.defaultTab)
              }
            >
              {selectedProductOption ? (
                <ProductsAttributesView
                  key={`attributes-${flagReloadKey}`}
                  productId={selectedProductOption.productId}
                  productType={selectedProductOption?.productType}
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  refetch={refetch}
                />
              ) : null}
            </Tab>
            <Tab
              eventKey={PRODUCT_DETAIL_VIEW_TABS.ASSETS}
              title={PRODUCT_DETAIL_VIEW_TABS.ASSETS}
              tabClassName={
                activeTabKey === PRODUCT_DETAIL_VIEW_TABS.ASSETS
                  ? css(styles.activeTab)
                  : css(styles.defaultTab)
              }
            >
              <GlobalProductAssetView
                key={`assets-${flagReloadKey}`}
                refetch={refetch}
                selectedProductOption={selectedProductOption}
                selectedProductOptions={selectedProductOptions}
              />
            </Tab>
            <Tab
              eventKey={PRODUCT_DETAIL_VIEW_TABS.RELATIONSHIPS}
              title={PRODUCT_DETAIL_VIEW_TABS.RELATIONSHIPS}
              tabClassName={
                activeTabKey === PRODUCT_DETAIL_VIEW_TABS.RELATIONSHIPS
                  ? css(styles.activeTab)
                  : css(styles.defaultTab)
              }
            >
              <GlobalProductsRelationshipsView globalProduct={globalProduct} refetch={refetch} />
            </Tab>
            <Tab
              eventKey={PRODUCT_DETAIL_VIEW_TABS.CHANGE_LOG}
              title={PRODUCT_DETAIL_VIEW_TABS.CHANGE_LOG}
              tabClassName={
                activeTabKey === PRODUCT_DETAIL_VIEW_TABS.CHANGE_LOG
                  ? css(styles.activeTab)
                  : css(styles.defaultTab)
              }
            >
              <GlobalProductsChangelogView selectedProductOption={selectedProductOption} />
            </Tab>
          </Tabs>
        </Col>
      </Row>

      <CloneVendorProductModal
        showCloneModal={showCloneModal}
        setShowCloneModal={setShowCloneModal}
        vendorProductId={selectedVendorProductId}
        refetch={refetch}
      />
      {showGlobalProductUpdateModal && (
        <GlobalProductUpdateModal
          onHide={() => setShowGlobalProductUpdateModal(false)}
          globalProduct={globalProduct}
          newGlobalProductId={globalProductIdToUpdate}
        />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '50vh',
  },
  attributeTitle: {
    fontWeight: 'bold',
    color: anchovy,
    marginBottom: 4,
  },
  attributeName: {
    display: 'inline',
    fontWeight: 'bold',
    color: anchovy,
  },
  attributeValue: {
    display: 'block',
  },
  leftCol: {
    paddingLeft: '1.9rem',
    minHeight: '100vh',
    borderRight: '3px solid ' + backgroundGrey4,
  },
  categoryValue: {
    display: 'block',
  },
  productSelect: {
    marginBottom: '0.8rem',
  },
});

export default GlobalProductsDetailView;
